import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, HostBinding, Input, OnDestroy,
	OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { Location, NgFor, NgIf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { pageTransitions } from '../../../../../../../shared/animations/page-transitions';
import { UserInfo } from '../../../../../../../shared/models/user-info';
import { OrderBy, OrderByDirection } from '../../../../../../../shared/utils/constant';
import { OrganizationServiceResponse } from '../../../../../../models/organization-service-info';
import { DragDropServices } from '../../../../../../models/user-service';
import { OrganizationService } from '../../../../../../services/organization.service';
import { AddUserService } from '../../../../../../services/add-user.service';
import { SlideButtonComponent } from '../../../../../../../shared/components/slide-button/slide-button.component';
import { LoadingSpinnerComponent } from '../../../../../../../shared/components/utils/loading-spinner.component';

const orgServiceFields = [
	'id',
	'service.id',
	'service.labelTranslationId',
	'service.descriptionTransId',
	'service.manageParameter',
	'supplier.id',
	'supplier.name',
	'serviceParameters.id',
	'serviceParameters.value',
	'serviceParameters.parameter.id',
	'serviceParameters.parameter.paramName',
	'serviceParameters.parameter.defaultValue',
	'serviceParameters.parameter.description'
];
const PAGE = 1;
const LIMIT = 10_000;
const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'service.id';
@Component({
	selector: 'copo-step-services-management',
	templateUrl: './step-services-management.component.html',
	styleUrl: './step-services-management.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		TranslateModule,
		CdkDropList,
		NgFor,
		CdkDrag,
		MatIconModule,
		NgIf,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule,
		SlideButtonComponent,
		LoadingSpinnerComponent
	],
})

export class StepServicesManagementComponent implements OnInit, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@Input() togglePosition = false;
	@Output() readonly typeOfTab = new EventEmitter<string>();
	@Output() readonly togglePositionChange = new EventEmitter<boolean>();
	searchTextControl = new FormControl('');
	allServices: DragDropServices[] = [];
	addedServices: DragDropServices[] = [];
	orgId?: string;
	userInfo?: UserInfo;
	orgServiceFilter = '';
	organizationServices?: OrganizationServiceResponse[];
	searchTextSubscription?: Subscription;
	userCardServiceSub: Subscription = Subscription.EMPTY;
	sub?: Subscription;
	selectedServices: DragDropServices[] = [];
	organizationServicesAreLoading = false;
	selectedServicesAreLoading: boolean;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly orgService: OrganizationService,
		private readonly location: Location,
		private readonly addUserService: AddUserService
	) {
		if (this.route.parent === null) {
			throw new Error('wrong url');
		}
		this.sub = this.addUserService.orgId
			.subscribe(orgId => {
				this.orgId = orgId;
			});
		this.getOrganizationServices(this.orgId);
		this.selectedServicesAreLoading = true;
		this.sub = this.addUserService.services.subscribe({
			next: data => {
				this.selectedServicesAreLoading = false;
				this.selectedServices = this.sortAllSelectedService(data);
			},
			error: (err: unknown) => {
				this.selectedServicesAreLoading = false;
				console.error(err);
			}
		});
		this.addedServices = this.selectedServices;
	}

	ngOnInit() {
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			const searchText = <string | undefined>value;
			if (searchText === undefined) {
				return;
			}
			if (searchText === '' && this.organizationServices) {
				this.getDragDropForOrg(this.organizationServices);
			} else {
				this.allServices = this.allServices.filter(allSer =>
					allSer.id === searchText || allSer.name.toLowerCase().includes(searchText.toLowerCase()));
			}
		});
	}

	onCancel() {
		this.location.back();
	}

	getOrganizationServices(orgID: string | undefined) {
		if (orgID) {
			this.organizationServicesAreLoading = true;
			this.userCardServiceSub = this.orgService.getOrganizationServices(
				orgID,
				orgServiceFields,
				this.orgServiceFilter,
				PAGE,
				LIMIT,
				new OrderBy(<keyof OrganizationServiceResponse> SORT_BY, SORT_WAY),
				false
			).subscribe({
				next: services => {
					this.organizationServicesAreLoading = false;
					this.organizationServices = services.results;
					this.organizationServices = this.organizationServices.filter(o1 =>
						!this.selectedServices.some(o2 => o1.service.id === o2.id));
					this.getDragDropForOrg(this.organizationServices);
				},
				error: (err: unknown) => {
					this.organizationServicesAreLoading = false;
					console.error(err);
				}
			});
		}
	}

	getDragDropForOrg(orgResponse: OrganizationServiceResponse[]) {
		this.allServices = [];
		for (const service of orgResponse) {
			const dragDropService: DragDropServices = {
				id: service.service.id,
				name: service.service.labelTranslationId,
				description: service.service.descriptionTransId,
			};
			this.allServices.push(dragDropService);
		}
		this.allServices = this.sortAllSelectedService(this.allServices);
	}

	drop(event: CdkDragDrop<DragDropServices[]>) {
		if (event.previousContainer === event.container) {
			moveItemInArray(
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		} else {
			transferArrayItem(
				event.previousContainer.data,
				event.container.data,
				event.previousIndex,
				event.currentIndex
			);
		}
		this.addUserService.updateServices(this.addedServices);
		this.sortAllSelectedService(this.addedServices);
	}

	onToggleChange() {
		this.typeOfTab.emit('Add all services');
		this.togglePositionChange.emit(true);
		this.addedServices.push(...this.allServices);
		this.addUserService.updateServices(this.addedServices);
		this.allServices = [];
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
		this.userCardServiceSub.unsubscribe();
	}

	private sortAllSelectedService(serviceData: DragDropServices[]): DragDropServices[] {
		return serviceData.sort((a, b) => a.name.localeCompare(b.name));
	}
}
